<template>
  <basic-card>
    <template #header> {{ internalDefinition.id ? '' : `(${$t('unsaved')})` }}</template>
    <form-wrapper :form-title="$t('reporting.defaultParams')">

      <form-text-input
          :caption="$t('reporting.name')"
          v-model="internalDefinition.name"
      />

      <form-number-input
          :caption="$t('reporting.limit')"
          v-model="internalDefinition.limit"
      />

      <form-select-input
          :caption="$t('reporting.limitCurrency')"
          :options="currenciesOptions"
          v-model="internalDefinition.limitCurrency"
      />

      <form-number-input
          :caption="$t('reporting.limitOP')"
          v-model="internalDefinition.limitOP"
      />

      <form-select-input
          :caption="$t('reporting.limitOPCurrency')"
          :options="currenciesOptions"
          v-model="internalDefinition.limitOPCurrency"
      />

      <form-expression-date-input
          :caption="$t('reporting.dateExpression')"
          v-model="internalDefinition.date"
      />

      <form-expression-date-input
          :caption="$t('reporting.startDateExpression')"
          v-model="internalDefinition.cashflowDateFrom"
      />

      <form-expression-date-input
          :caption="$t('reporting.endDateExpression')"
          v-model="internalDefinition.cashflowDateTo"
      />

      <form-decimal-input
          v-model="internalDefinition.quantile"
          :caption="`${$t('reporting.quantile')}`"
          :precision="5"/>

      <form-multi-select-input :caption="$t('reporting.cashEventQueries')"
                               :options="cashEventQueryOptions.map(val => ({id: val.id, name: val.name}))"
                               v-model="internalDefinition.cashEventsQueries"/>

      <form-multi-select-input :caption="$t('reporting.cashEventRemoveQueries')"
                               :options="cashEventQueryOptions.map(val => ({id: val.id, name: val.name}))"
                               v-model="internalDefinition.cashEventsRemoveQueries"/>


      <form-select-input :caption="$t('reporting.accountQuery')"
                         :options="accountQueryOptions"
                         v-model="internalDefinition.accountQuery"
      />


    </form-wrapper>


    <form-wrapper :form-title="$t('reporting.params')">
      <form-select-input
          :caption="$t('reporting.currency')"
          :options="currenciesOptions"
          v-model="internalDefinition.currency"
      />


      <form-select-input
          :caption="$t('reporting.volatility')"
          :options="volatilitiesOptions"
          v-model="internalDefinition.volatilityId"
      />

      <form-select-input
          :caption="`${$t('reporting.gridPoint')}`"
          :options="gridPointOptions"
          v-model="internalDefinition.gridPoint"
      />

    </form-wrapper>

    <simple-horizontal-layout>
      <simple-button @press="emitSave">{{ $t("cashflow.definition.save") }}</simple-button>
      <simple-button @press="emitCalculation">{{ $t("reporting.calculate") }}</simple-button>
      <empty/>
    </simple-horizontal-layout>

    <framed-content :title="$t('reporting.results')">
      <data-table
          :data="results"
          :configuration="resultsTableConfig"
          @remove="removeResult"
      />
    </framed-content>

  </basic-card>
</template>

<script>
import BasicCard from "../components/layout/BasicCard";
import SimpleHorizontalLayout from "../components/layout/SimpleHorizontalLayout";
import SimpleButton from "../components/button/SimpleButton";
import FormTextInput from "../components/form/FormTextInput";
import FormSelectInput from "../components/form/FormSelectInput";
import {mapGetters} from "vuex";
import FormWrapper from "../components/form/FormWrapper";
import CollapsibleContent from "../components/CollapsibleContent";
import FormExpressionDateInput from "../components/form/FormDateExpressionInput";
import FormDecimalInput from "@/components/form/FormDecimalInput";
import Empty from "@/components/layout/Empty";
import Axios from "axios";
import DataTable from "@/components/table/DataTable";
import FramedContent from "@/components/layout/FramedContent";
import FormNumberInput from "@/components/form/FormNumberInput";
import FormMultiSelectInput from "@/components/form/FormMultiSelectInput";

export default {
  name: "VarReportDefinitionCard",
  components: {
    FormMultiSelectInput,
    FormNumberInput,
    FramedContent,
    DataTable,
    Empty,
    FormDecimalInput,
    FormExpressionDateInput,
    CollapsibleContent,
    FormWrapper,
    FormSelectInput,
    FormTextInput,
    SimpleButton,
    SimpleHorizontalLayout,
    BasicCard
  },
  props: {
    definition: Object
  },
  data: function () {
    return {
      internalDefinition: {
        cashEventsQueries: [],
        cashEventRemoveQueries: [],
        accountQuery: null,
        currency: null,
        volatilityId: null,
        gridPoint: null
      },

      results: [],
    }
  },
  computed: {
    ...
        mapGetters({
          accountQueries: "accountQueries/accountQueries",
          cashEventQueries: "cashEventQueries/cashEventQueries",
          currenciesOptions: "currencies/currenciesOptions",
          getRate: "currencies/getRate",
          volatilities: "volatilities/volatilities",
          volatilitiesOptions: "volatilities/volatilitiesOptions",
        }),
    resultsTableConfig() {
      return {
        columns: [
          {
            caption: this.$t("reporting.date"),
            datum: d => this.$moment(d.calculationTimestamp).format('DD.MM.YYYY HH:mm')
          },
          {
            caption: this.$t("reporting.dateFrom"),
            datum: d => this.$moment(d.dateFrom).format('DD.MM.YYYY')
          },
          {
            caption: this.$t("reporting.dateThru"),
            datum: d => this.$moment(d.dateTo).format('DD.MM.YYYY')
          },
          {
            caption: this.$t("reporting.exchangeRate"),
            datum: d => this.$formats.formatAmount(d.exchangeRate)
          },
          {
            caption: this.$t("reporting.openPosition"),
            datum: d => this.$formats.formatAmount(d.openPosition)
          },
          {
            caption: this.$t("reporting.result"),
            datum: d => this.$formats.formatAmount(d.result)
          },
          {
            caption: this.$t("reporting.limit") + (this.internalDefinition ? `[${this.internalDefinition.limitCurrency}]` : ``),
            datum: d => this.$formats.formatAmount(d.limit)
          },
          {
            caption: this.$t("reporting.limitOP") + (this.internalDefinition ? `[${this.internalDefinition.limitOPCurrency}]` : ``),
            datum: d => this.$formats.formatAmount(d.limitOP)
          },
          {
            caption: this.$t("reporting.limitOpOk"),
            datum: d => {
              const rate = this.getRate(this.internalDefinition.limitOPCurrency, this.internalDefinition.currency, d.calculationTimestamp);
              return (d.limitOP > d.openPosition * rate) ? this.$t("yes") : this.$t("no")
            }
          },
          {
            caption: this.$t("reporting.limitOk"),
            datum: d => {
              const rate = this.getRate(this.internalDefinition.limitCurrency, this.internalDefinition.currency, d.calculationTimestamp);
              return (d.limit > d.result * rate) ? this.$t("yes") : this.$t("no")
            }
          },
          {
            caption: this.$t("reporting.volatility"),
            datum: d => this.$formats.formatAmount(d.volatility)
          },
        ],
        actions: [
          {
            icon: 'trash',
            name: 'remove',
            tooltip: this.$t('reporting.deleteResult')
          }
        ]
      }
    },
    cashEventQueryOptions() {
      return this.cashEventQueries.map(query => ({
        id: query.id,
        name: query.name
      }))
    },

    accountQueryOptions() {
      return this.accountQueries.map(query => ({
        id: query.id,
        name: query.name
      }))
    },

    gridPointOptions() {
      return this.volatilities.find(vol => vol.id === this.internalDefinition.volatilityId)?.gridPoints?.map(gp => ({
        id: gp.name,
        name: gp.name
      })) ?? [];
    }

  },

  async mounted() {
    if (this.definition.id) {
      this.$set(this, 'internalDefinition', this.$_.cloneDeep(this.definition))
      if (this.internalDefinition.id) {
        await this.refreshResults()
      }
    }
  },

  methods: {
    async removeResult(result) {
      await Axios.delete(`/value-at-risk/api/v1/var/results/${result.id}`);
      this.$notify({
        group: 'application',
        title: `Successfully deleted result`,
        text: `Result for ${result.calculationTimestamp} was deleted`,
        type: "ok"
      })
      await this.refreshResults();
    },
    emitSave() {
      this.$emit('save', this.internalDefinition);
    },
    async refreshResults() {
      const response = await Axios.get(`/value-at-risk/api/v1/var/results?definitionId=${this.internalDefinition.id}&size=5000`);
      this.results = response.data.sort((a, b) => this.$moment(b.calculationTimestamp).diff(this.$moment(a.calculationTimestamp)));
    },
    async emitCalculation() {

      this.$emit('save', this.internalDefinition, true, async () => {
        try {
          const calculated = this.$_.cloneDeep(this.internalDefinition);
          calculated.quantile = this.internalDefinition.quantile / 100000;
          const response = await Axios.post(`/value-at-risk/api/v1/var/calculate`, calculated);
          await this.refreshResults();

          this.$notify({
            group: 'application',
            title: this.$t('reporting.calculateSuccess'),
            text: this.$t('reporting.calculateSuccess'),
            type: "ok"
          })
        } catch (e) {
          this.$notify({
            group: 'application',
            title: this.$t('reporting.calculateError'),
            text: this.$t('reporting.calculateError'),
            type: "error"
          })

        }

      });


    }
  }
}
</script>

<style lang="scss" scoped>
@import "../styles/global";

.columns-container {

  background-color: var(--input-background-color);
  border: 1px solid var(--input-border-color);
  border-radius: 5px;
  margin: 10px;
  padding: 5px;
}

.columns-item {
  background-color: var(--input-list-background-color);
  color: var(--input-active-font-color);
  border-radius: 5px;
  padding: 5px;
  margin: 5px;
  user-select: none;
  cursor: grab;
}

.drop-area {
  min-height: 20px;
}

select {
  width: 100%;
  height: 90px;
  background-color: var(--input-list-background-color);
  border: 1px solid var(--input-border-color);
  color: var(--input-active-font-color);
}

select option:checked {
  background-color: $openRed !important;
}

.selection-grid {
  display: grid;
  grid-template: 1fr / 1fr min-content 1fr;
  grid-gap: 10px;
}

.selection-arrows {
  display: flex;
  flex-flow: column;
  justify-items: center;
  justify-content: center;
  align-content: center;
  align-items: center;
}
</style>
